import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('GBP'); // Default to GBP
  
  // Exchange rates (base: GBP)
  const exchangeRates = {
    GBP: 1.0,
    USD: 1.27, // 1 GBP = 1.27 USD
    EUR: 1.20  // 1 GBP = 1.20 EUR
  };

  const currencySymbols = {
    GBP: '£',
    USD: '$',
    EUR: '€'
  };

  const currencyNames = {
    GBP: 'British Pound',
    USD: 'US Dollar', 
    EUR: 'Euro'
  };

  // Auto-detect currency from browser locale, with localStorage override
  useEffect(() => {
    const savedCurrency = localStorage.getItem('vlogclip_currency');
    if (savedCurrency && exchangeRates[savedCurrency]) {
      setCurrency(savedCurrency);
      return;
    }
    // Auto-detect from browser locale
    const localeToCurrency = {
      'en-US': 'USD', 'en-CA': 'CAD', 'en-AU': 'AUD', 'en-NZ': 'NZD',
      'en-GB': 'GBP', 'en-IE': 'EUR', 'en-SG': 'SGD', 'en-ZA': 'ZAR',
      'en-IN': 'INR', 'fr-FR': 'EUR', 'fr-CA': 'CAD', 'de-DE': 'EUR',
      'de-AT': 'EUR', 'de-CH': 'CHF', 'es-ES': 'EUR', 'es-MX': 'MXN',
      'it-IT': 'EUR', 'pt-BR': 'BRL', 'pt-PT': 'EUR', 'ja-JP': 'JPY',
      'sv-SE': 'SEK', 'nb-NO': 'NOK', 'da-DK': 'DKK', 'nl-NL': 'EUR',
      'fi-FI': 'EUR', 'pl-PL': 'EUR', 'hi-IN': 'INR', 'zh-SG': 'SGD'
    };
    const langToCurrency = {
      'en': 'USD', 'fr': 'EUR', 'de': 'EUR', 'es': 'EUR', 'it': 'EUR',
      'pt': 'EUR', 'ja': 'JPY', 'sv': 'SEK', 'nb': 'NOK', 'da': 'DKK',
      'nl': 'EUR', 'hi': 'INR', 'zh': 'USD'
    };
    const locale = navigator.language || navigator.userLanguage || '';
    const detected = localeToCurrency[locale] || langToCurrency[locale.split('-')[0]] || 'GBP';
    if (exchangeRates[detected]) {
      setCurrency(detected);
      localStorage.setItem('vlogclip_currency', detected);
    }
  }, []);

  // Save currency to localStorage when changed
  useEffect(() => {
    localStorage.setItem('vlogclip_currency', currency);
  }, [currency]);

  const convertPrice = (gbpPrice) => {
    const convertedPrice = gbpPrice * exchangeRates[currency];
    return Math.round(convertedPrice * 100) / 100; // Round to 2 decimal places
  };

  const formatPrice = (gbpPrice) => {
    const convertedPrice = convertPrice(gbpPrice);
    const symbol = currencySymbols[currency];
    
    // Format with proper decimals
    if (convertedPrice % 1 === 0) {
      return `${symbol}${convertedPrice.toFixed(0)}`;
    } else {
      return `${symbol}${convertedPrice.toFixed(2)}`;
    }
  };

  const value = {
    currency,
    setCurrency,
    exchangeRates,
    currencySymbols,
    currencyNames,
    convertPrice,
    formatPrice
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};